/**
 * Create Zone Modal Component
 * Modal for creating new plant zones
 */

import React, { useState, useRef, useCallback } from 'react';
import { X, Square, Palette, MousePointer } from 'lucide-react';
import { PlantLayout } from '../../services/plantMapping';
import { usePlantMappingStore } from '../../stores/plantMappingStore';

interface CreateZoneModalProps {
  layout: PlantLayout;
  onClose: () => void;
}

const CreateZoneModal: React.FC<CreateZoneModalProps> = ({ layout, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    x1: 0.1,
    y1: 0.1,
    x2: 0.4,
    y2: 0.4,
    color: '#3b82f6',
    opacity: 0.2,
    border_color: '#1e40af',
    border_style: 'dashed',
    capacity: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [showManualCoordinates, setShowManualCoordinates] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  
  const { createZone, loading } = usePlantMappingStore();

  // Predefined colors
  const colorOptions = [
    { name: 'Blue', color: '#3b82f6', border: '#1e40af' },
    { name: 'Green', color: '#10b981', border: '#047857' },
    { name: 'Purple', color: '#8b5cf6', border: '#7c3aed' },
    { name: 'Orange', color: '#f59e0b', border: '#d97706' },
    { name: 'Red', color: '#ef4444', border: '#dc2626' },
    { name: 'Pink', color: '#ec4899', border: '#db2777' },
    { name: 'Indigo', color: '#6366f1', border: '#4f46e5' },
    { name: 'Teal', color: '#14b8a6', border: '#0d9488' }
  ];

  const borderStyleOptions = [
    { value: 'solid', label: 'Solid' },
    { value: 'dashed', label: 'Dashed' },
    { value: 'dotted', label: 'Dotted' }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Interactive drawing handlers
  const handleMouseDown = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    setDrawStart({ x, y });
    setIsDrawing(true);
  }, []);

  const handleMouseMove = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    if (!isDrawing || !drawStart) return;

    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    // Update coordinates in real-time
    const x1 = Math.min(drawStart.x, x);
    const y1 = Math.min(drawStart.y, y);
    const x2 = Math.max(drawStart.x, x);
    const y2 = Math.max(drawStart.y, y);

    setFormData(prev => ({
      ...prev,
      x1: Math.max(0, Math.min(1, x1)),
      y1: Math.max(0, Math.min(1, y1)),
      x2: Math.max(0, Math.min(1, x2)),
      y2: Math.max(0, Math.min(1, y2))
    }));
  }, [isDrawing, drawStart]);

  const handleMouseUp = useCallback(() => {
    setIsDrawing(false);
    setDrawStart(null);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDrawing(false);
    setDrawStart(null);
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Zone name is required';
    }

    if (formData.x1 >= formData.x2) {
      newErrors.coordinates = 'Invalid coordinates: x1 must be less than x2';
    }

    if (formData.y1 >= formData.y2) {
      newErrors.coordinates = 'Invalid coordinates: y1 must be less than y2';
    }

    if (formData.capacity && parseInt(formData.capacity) < 1) {
      newErrors.capacity = 'Capacity must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await createZone({
        name: formData.name,
        description: formData.description,
        x1: formData.x1,
        y1: formData.y1,
        x2: formData.x2,
        y2: formData.y2,
        color: formData.color,
        opacity: formData.opacity,
        border_color: formData.border_color,
        border_style: formData.border_style,
        capacity: formData.capacity ? parseInt(formData.capacity) : null
      });
      onClose();
    } catch (error) {
      console.error('Failed to create zone:', error);
    }
  };

  const selectColor = (colorOption: typeof colorOptions[0]) => {
    setFormData(prev => ({
      ...prev,
      color: colorOption.color,
      border_color: colorOption.border
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Create New Zone</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Zone Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Production Line A"
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capacity (optional)
              </label>
              <input
                type="number"
                value={formData.capacity}
                onChange={(e) => handleInputChange('capacity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Max workshops"
                min="1"
              />
              {errors.capacity && (
                <p className="text-red-500 text-xs mt-1">{errors.capacity}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Optional description of this zone"
            />
          </div>

          {/* Coordinates - Collapsible */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Manual Coordinates (Optional)
              </label>
              <button
                type="button"
                onClick={() => setShowManualCoordinates(!showManualCoordinates)}
                className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-200"
              >
                {showManualCoordinates ? 'Hide Manual Input' : 'Show Manual Input'}
              </button>
            </div>
            <div className="text-xs text-gray-500 mb-3 p-2 bg-blue-50 rounded border-l-4 border-blue-200">
              💡 <strong>Tip:</strong> Use the interactive drawing area above - it's much easier than entering coordinates manually!
            </div>
            
            {showManualCoordinates && (
              <div>
            
            {/* Visual Coordinate Helper */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-600 mb-2">
                <strong>How to set coordinates:</strong>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                <div>
                  <strong>X Coordinates (Left ↔ Right):</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• 0.0 = Far Left</li>
                    <li>• 0.5 = Center</li>
                    <li>• 1.0 = Far Right</li>
                  </ul>
                </div>
                <div>
                  <strong>Y Coordinates (Top ↔ Bottom):</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• 0.0 = Top</li>
                    <li>• 0.5 = Middle</li>
                    <li>• 1.0 = Bottom</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Quick Presets */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-2">Quick Presets:</label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('x1', 0.1);
                    handleInputChange('y1', 0.1);
                    handleInputChange('x2', 0.4);
                    handleInputChange('y2', 0.4);
                  }}
                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Top-Left Quarter
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('x1', 0.6);
                    handleInputChange('y1', 0.1);
                    handleInputChange('x2', 0.9);
                    handleInputChange('y2', 0.4);
                  }}
                  className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                >
                  Top-Right Quarter
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('x1', 0.1);
                    handleInputChange('y1', 0.6);
                    handleInputChange('x2', 0.4);
                    handleInputChange('y2', 0.9);
                  }}
                  className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                >
                  Bottom-Left Quarter
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('x1', 0.6);
                    handleInputChange('y1', 0.6);
                    handleInputChange('x2', 0.9);
                    handleInputChange('y2', 0.9);
                  }}
                  className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                >
                  Bottom-Right Quarter
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('x1', 0.2);
                    handleInputChange('y1', 0.3);
                    handleInputChange('x2', 0.8);
                    handleInputChange('y2', 0.7);
                  }}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Center Large
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1 h-8 flex flex-col justify-center">
                  <span>Top-Left X</span>
                  <span className="text-gray-400">({(formData.x1 * 100).toFixed(0)}% from left)</span>
                </label>
                <input
                  type="number"
                  value={formData.x1}
                  onChange={(e) => handleInputChange('x1', parseFloat(e.target.value))}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  min="0"
                  max="1"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1 h-8 flex flex-col justify-center">
                  <span>Top-Left Y</span>
                  <span className="text-gray-400">({(formData.y1 * 100).toFixed(0)}% from top)</span>
                </label>
                <input
                  type="number"
                  value={formData.y1}
                  onChange={(e) => handleInputChange('y1', parseFloat(e.target.value))}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  min="0"
                  max="1"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1 h-8 flex flex-col justify-center">
                  <span>Bottom-Right X</span>
                  <span className="text-gray-400">({(formData.x2 * 100).toFixed(0)}% from left)</span>
                </label>
                <input
                  type="number"
                  value={formData.x2}
                  onChange={(e) => handleInputChange('x2', parseFloat(e.target.value))}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  min="0"
                  max="1"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1 h-8 flex flex-col justify-center">
                  <span>Bottom-Right Y</span>
                  <span className="text-gray-400">({(formData.y2 * 100).toFixed(0)}% from top)</span>
                </label>
                <input
                  type="number"
                  value={formData.y2}
                  onChange={(e) => handleInputChange('y2', parseFloat(e.target.value))}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  min="0"
                  max="1"
                  step="0.01"
                />
              </div>
            </div>
            {errors.coordinates && (
              <p className="text-red-500 text-xs mt-1">{errors.coordinates}</p>
            )}
              </div>
            )}
          </div>

          {/* Visual Properties */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Visual Properties
            </label>
            
            {/* Color Selection */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-2">Zone Color</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((option) => (
                  <button
                    key={option.name}
                    type="button"
                    onClick={() => selectColor(option)}
                    className={`w-8 h-8 rounded border-2 ${
                      formData.color === option.color 
                        ? 'border-gray-800' 
                        : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: option.color }}
                    title={option.name}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Opacity</label>
                <input
                  type="range"
                  value={formData.opacity}
                  onChange={(e) => handleInputChange('opacity', parseFloat(e.target.value))}
                  className="w-full"
                  min="0"
                  max="1"
                  step="0.1"
                />
                <div className="text-xs text-gray-500 text-center">
                  {(formData.opacity * 100).toFixed(0)}%
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Border Style</label>
                <select
                  value={formData.border_style}
                  onChange={(e) => handleInputChange('border_style', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  {borderStyleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Custom Color</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  className="w-full h-8 border border-gray-300 rounded"
                />
              </div>
            </div>
          </div>

          {/* Interactive Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MousePointer className="inline w-4 h-4 mr-1" />
              Interactive Zone Creator
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Interactive Zone Preview */}
              <div className="border border-gray-300 rounded p-4 bg-gray-50">
                <div className="text-xs text-gray-600 mb-2 text-center">
                  <div className="font-medium">Click and drag to draw your zone</div>
                  <div className="text-gray-500 mt-1">
                    {isDrawing ? 'Drawing...' : 'Start from any corner and drag to opposite corner'}
                  </div>
                </div>
                <svg 
                  ref={svgRef}
                  width="200" 
                  height="120" 
                  className="border bg-white cursor-crosshair"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Grid lines for reference */}
                  <defs>
                    <pattern id="grid" width="20" height="12" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 12" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
                    </pattern>
                  </defs>
                  <rect width="200" height="120" fill="url(#grid)" />
                  
                  {/* Zone rectangle */}
                  <rect
                    x={formData.x1 * 200}
                    y={formData.y1 * 120}
                    width={(formData.x2 - formData.x1) * 200}
                    height={(formData.y2 - formData.y1) * 120}
                    fill={formData.color}
                    opacity={formData.opacity}
                    stroke={formData.border_color}
                    strokeWidth="2"
                    strokeDasharray={
                      formData.border_style === 'dashed' ? '5,3' :
                      formData.border_style === 'dotted' ? '2,2' : 'none'
                    }
                  />
                  
                  {/* Zone name */}
                  <text
                    x={((formData.x1 + formData.x2) / 2) * 200}
                    y={formData.y1 * 120 - 5}
                    textAnchor="middle"
                    fontSize="10"
                    fill={formData.border_color}
                  >
                    {formData.name || 'Zone Name'}
                  </text>
                  
                  {/* Coordinate labels */}
                  <text x="5" y="15" fontSize="8" fill="#666">0,0</text>
                  <text x="175" y="15" fontSize="8" fill="#666">1,0</text>
                  <text x="5" y="115" fontSize="8" fill="#666">0,1</text>
                  <text x="175" y="115" fontSize="8" fill="#666">1,1</text>
                </svg>
              </div>
              
              {/* Coordinate Guide */}
              <div className="border border-gray-300 rounded p-4 bg-blue-50">
                <div className="text-xs text-gray-700 mb-2 font-medium">Coordinate System Guide</div>
                <svg width="200" height="120" className="border bg-white">
                  {/* Plant layout representation */}
                  <rect width="200" height="120" fill="#f8f9fa" stroke="#dee2e6" strokeWidth="1"/>
                  
                  {/* Example zones */}
                  <rect x="20" y="15" width="60" height="40" fill="#e3f2fd" stroke="#1976d2" strokeWidth="1" strokeDasharray="3,2"/>
                  <text x="50" y="32" fontSize="8" textAnchor="middle" fill="#1976d2">Production</text>
                  
                  <rect x="120" y="15" width="60" height="40" fill="#f3e5f5" stroke="#7b1fa2" strokeWidth="1" strokeDasharray="3,2"/>
                  <text x="150" y="32" fontSize="8" textAnchor="middle" fill="#7b1fa2">Quality</text>
                  
                  <rect x="20" y="70" width="160" height="30" fill="#fff3e0" stroke="#f57c00" strokeWidth="1" strokeDasharray="3,2"/>
                  <text x="100" y="87" fontSize="8" textAnchor="middle" fill="#f57c00">Warehouse</text>
                  
                  {/* Coordinate arrows */}
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
                    </marker>
                  </defs>
                  
                  {/* X axis arrow */}
                  <line x1="10" y1="110" x2="190" y2="110" stroke="#666" strokeWidth="1" markerEnd="url(#arrowhead)"/>
                  <text x="100" y="125" fontSize="8" textAnchor="middle" fill="#666">X (Left → Right)</text>
                  
                  {/* Y axis arrow */}
                  <line x1="10" y1="110" x2="10" y2="10" stroke="#666" strokeWidth="1" markerEnd="url(#arrowhead)"/>
                  <text x="5" y="60" fontSize="8" textAnchor="middle" fill="#666" transform="rotate(-90, 5, 60)">Y (Top → Bottom)</text>
                </svg>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Square size={16} />
                  Create Zone
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateZoneModal;
